import React, { useEffect, useRef } from 'react';

/**
 * MatrixRainBackdrop
 * ------------------
 * Auto-playing green matrix-style numeric rain, constrained to its parent's
 * bounds (absolute-positioned + inset-0). Blended with a faint diagonal
 * light-ray sweep for depth. Intended to sit behind the Landing-page navbar
 * ONLY — keep it strictly cosmetic (pointer-events: none, aria-hidden).
 *
 * Perf notes:
 *  - Single <canvas>, single rAF loop. Draws at device pixel ratio.
 *  - ResizeObserver keeps the canvas in sync with the navbar strip (~64px tall
 *    on desktop, wider on mobile scroll). Column array recomputed on resize.
 *  - Uses a trail-trick (filling with semi-transparent black each frame) so
 *    characters fade downward — no per-character history array.
 *  - Light rays are a pure CSS sibling layer; zero extra canvas cost.
 */
export const MatrixRainBackdrop = () => {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    // Binary rain only — strictly 0s and 1s per product brief.
    const GLYPHS = '01';
    const FONT_SIZE = 14; // px in CSS space
    // Step columns forward every Nth rAF frame for a very slow cadence so the
    // light-ray sweep underneath is clearly perceivable.
    const ADVANCE_EVERY_FRAMES = 10;
    let frameCounter = 0;
    let columns = [];
    let width = 0;
    let height = 0;
    let dpr = Math.max(1, window.devicePixelRatio || 1);

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      width = Math.max(1, Math.floor(rect.width));
      height = Math.max(1, Math.floor(rect.height));
      dpr = Math.max(1, window.devicePixelRatio || 1);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      // One column per glyph-width. Start each column at a random y offset so
      // the rain looks mid-flight on first paint (never empty + obviously-new).
      const cols = Math.max(1, Math.floor(width / FONT_SIZE));
      columns = new Array(cols).fill(0).map(() => Math.random() * (height / FONT_SIZE));
      // Clear so leftover trail from prior size doesn't streak.
      ctx.clearRect(0, 0, width, height);
    };

    resize();

    // Observe parent resizes (nav height may change between mobile/desktop).
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const draw = () => {
      // Throttle column advancement so the rain is slow-medium paced — the
      // CSS light-ray sweep (9s loop) needs breathing room to be noticed.
      // We still run rAF every frame so the trail fade stays buttery-smooth,
      // but glyphs only step forward every `ADVANCE_EVERY_FRAMES` frames.
      frameCounter += 1;
      const shouldAdvance = frameCounter % ADVANCE_EVERY_FRAMES === 0;

      // Fade the previous frame — this is what produces the vertical trail.
      // Lower alpha = longer trail. We keep it slightly transparent so the
      // navbar's own dark backdrop stays visible underneath.
      ctx.fillStyle = 'rgba(0, 0, 0, 0.06)';
      ctx.fillRect(0, 0, width, height);

      if (shouldAdvance) {
        ctx.font = `${FONT_SIZE}px "Fira Code", "Menlo", monospace`;
        ctx.textBaseline = 'top';

        for (let i = 0; i < columns.length; i += 1) {
          const ch = GLYPHS.charAt(Math.floor(Math.random() * GLYPHS.length));
          const x = i * FONT_SIZE;
          const y = columns[i] * FONT_SIZE;

          // Head of the stream is bright white-green; tail is darker green.
          // Randomly make a column's head extra bright once in a while for
          // "glitch" highlights.
          const isHead = Math.random() > 0.975;
          ctx.fillStyle = isHead ? '#CCFFCC' : '#00FF41';
          ctx.fillText(ch, x, y);

          // Reset a column once it runs off the bottom — with a random chance,
          // so all columns don't loop in lockstep.
          if (y > height && Math.random() > 0.965) {
            columns[i] = 0;
          } else {
            columns[i] += 1;
          }
        }
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
    };
  }, []);

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 overflow-hidden"
      data-testid="matrix-navbar-backdrop"
    >
      {/* Matrix canvas — behind everything. Tuned opacity so the 0s/1s
          read clearly against the navbar backdrop. */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full opacity-45"
      />
      {/* Subtle top/bottom vignette so navbar text stays readable over the rain. */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/10 to-black/60" />
    </div>
  );
};

export default MatrixRainBackdrop;
